import {CSSProperties, MouseEvent, PointerEvent, useEffect, useRef, useState} from 'react'

/**
 * Switch: `true` — Versi's launcher and floating panel can be dragged anywhere on screen (so
 * they can be moved off a table they cover). `false` — both stay pinned to the bottom-right
 * corner, as before.
 */
export const CHATBOT_DRAG_ENABLED = true

/** Pointer travel (px) before a press becomes a drag; below it a press is still a click. */
const DRAG_THRESHOLD = 5

type Position = {left: number; top: number}

type Options = {
  /** localStorage key the dropped position is remembered under (per browser). */
  storageKey: string
  /** Off: no dragging and the saved position is ignored (the element keeps its CSS spot). */
  enabled: boolean
  /** Only presses inside this selector start a drag (e.g. the panel header). Default: anywhere. */
  handleSelector?: string
  /** Presses inside this selector never start a drag (e.g. the header's buttons). */
  ignoreSelector?: string
}

type DragState = {
  pointerId: number
  startX: number
  startY: number
  originLeft: number
  originTop: number
  moved: boolean
  transition: string
  /** Where the element is heading (it glides there, so its rect lags behind). */
  last: Position | null
}

/** Class on the element while it is dragged — the lift / shadow animation in _aichatbot.scss. */
const DRAGGING_CLASS = 'is-dragging'

/** Short glide after the pointer: smooth, but still feels attached to the cursor. */
const DRAG_TRANSITION = 'left 90ms ease-out, top 90ms ease-out, box-shadow 200ms ease'

const readPosition = (key: string): Position | null => {
  try {
    const raw = window.localStorage.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return Number.isFinite(parsed?.left) && Number.isFinite(parsed?.top) ? parsed : null
  } catch {
    return null
  }
}

const writePosition = (key: string, position: Position) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(position))
  } catch {
    // Storage blocked (private window): the position just isn't remembered.
  }
}

/** Below the fixed app header, so a dragged element never hides under it. */
const minTop = () => {
  const header = document.getElementById('kt_app_header')
  return header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0
}

/** Keeps the whole element on screen (and below the header). */
const clamp = ({left, top}: Position, element: HTMLElement): Position => {
  const {width, height} = element.getBoundingClientRect()
  const topLimit = minTop()
  return {
    left: Math.round(Math.min(Math.max(0, left), Math.max(0, window.innerWidth - width))),
    top: Math.round(Math.min(Math.max(topLimit, top), Math.max(topLimit, window.innerHeight - height))),
  }
}

/**
 * Pointer-drag for a `position: fixed` element. Spread `handlers` on the element and merge
 * `positionStyle` into its style; `ref` must point at it. The position is physical px
 * (left/top), so it behaves the same in RTL. While dragging, the element is moved directly in
 * the DOM (no re-render per move) and committed to state on release. A press that moved past
 * the threshold swallows the click that follows, so a dragged launcher does not also open.
 */
const useFloatingDrag = <T extends HTMLElement>({
  storageKey,
  enabled,
  handleSelector,
  ignoreSelector,
}: Options) => {
  const ref = useRef<T>(null)
  const drag = useRef<DragState | null>(null)
  const swallowClick = useRef(false)
  const restoreTransition = useRef<(() => void) | null>(null)
  const [position, setPosition] = useState<Position | null>(() => readPosition(storageKey))

  // Pull a remembered position back on screen after the window shrinks (or on first paint,
  // if it was saved on a bigger screen).
  useEffect(() => {
    if (!enabled || !position) return
    const reclamp = () => {
      const element = ref.current
      if (!element) return
      const next = clamp(position, element)
      if (next.left !== position.left || next.top !== position.top) setPosition(next)
    }
    reclamp()
    window.addEventListener('resize', reclamp)
    return () => window.removeEventListener('resize', reclamp)
  }, [enabled, position])

  const onPointerDown = (event: PointerEvent<T>) => {
    const element = ref.current
    if (!enabled || !element || event.button !== 0) return
    const target = event.target as HTMLElement
    if (handleSelector && !target.closest(handleSelector)) return
    if (ignoreSelector && target.closest(ignoreSelector)) return
    // A new press right after a drop: put the element's own transition back first, so it is
    // what this drag saves and restores.
    restoreTransition.current?.()
    const rect = element.getBoundingClientRect()
    drag.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originLeft: rect.left,
      originTop: rect.top,
      moved: false,
      transition: element.style.transition,
      last: null,
    }
  }

  const onPointerMove = (event: PointerEvent<T>) => {
    const state = drag.current
    const element = ref.current
    if (!state || !element || event.pointerId !== state.pointerId) return
    const dx = event.clientX - state.startX
    const dy = event.clientY - state.startY
    if (!state.moved) {
      if (Math.hypot(dx, dy) < DRAG_THRESHOLD) return
      state.moved = true
      // Captured only once it is a drag: capturing on press would retarget a plain click.
      element.setPointerCapture(event.pointerId)
      // Pin to the current spot in left/top before the glide starts, so it doesn't jump.
      element.style.transition = 'none'
      element.style.left = `${state.originLeft}px`
      element.style.top = `${state.originTop}px`
      element.style.right = 'auto'
      element.style.bottom = 'auto'
      void element.offsetWidth
      element.style.transition = DRAG_TRANSITION
      element.classList.add(DRAGGING_CLASS)
      document.body.style.userSelect = 'none'
    }
    const next = clamp({left: state.originLeft + dx, top: state.originTop + dy}, element)
    state.last = next
    element.style.left = `${next.left}px`
    element.style.top = `${next.top}px`
  }

  const endDrag = (event: PointerEvent<T>) => {
    const state = drag.current
    const element = ref.current
    if (!state || event.pointerId !== state.pointerId) return
    drag.current = null
    if (!state.moved || !element) return
    if (element.hasPointerCapture(event.pointerId)) element.releasePointerCapture(event.pointerId)
    element.classList.remove(DRAGGING_CLASS)
    document.body.style.userSelect = ''
    // Let the last glide finish before the element's own transition comes back.
    restoreTransition.current = () => {
      element.style.transition = state.transition
      restoreTransition.current = null
    }
    window.setTimeout(() => restoreTransition.current?.(), 200)
    // The click (if the browser fires one) follows pointerup in the same task; clear the flag
    // after it so it can never eat a later, real click.
    swallowClick.current = true
    window.setTimeout(() => {
      swallowClick.current = false
    }, 0)
    const rect = element.getBoundingClientRect()
    const next = state.last ?? {left: Math.round(rect.left), top: Math.round(rect.top)}
    setPosition(next)
    writePosition(storageKey, next)
  }

  const onClickCapture = (event: MouseEvent<T>) => {
    if (!swallowClick.current) return
    swallowClick.current = false
    event.preventDefault()
    event.stopPropagation()
  }

  const isPlaced = enabled && position !== null
  const positionStyle: CSSProperties = isPlaced
    ? {left: position.left, top: position.top, right: 'auto', bottom: 'auto'}
    : {}

  return {
    ref,
    /** True when a dragged position is applied — drop CSS that pins the element to a corner. */
    isPlaced,
    positionStyle,
    handlers: enabled
      ? {
          onPointerDown,
          onPointerMove,
          onPointerUp: endDrag,
          onPointerCancel: endDrag,
          onClickCapture,
        }
      : {},
  }
}

export default useFloatingDrag
